// src/layouts/TestReminderFields.js
//
// Bloque de configuración del recordatorio automático, compartido por todas las
// pantallas de envío de tests (DISC, capacidades, habilidades personales, ventas),
// tanto individuales como masivas.
//
// Sólo se muestra cuando el envío del correo es automático: si el consultor manda
// el link por su cuenta, el recordatorio saldría desde otro remitente que el
// candidato nunca vio, así que esos casos quedan fuera del circuito.
//
// Los topes se revalidan en el backend (utils/testReminder.utils.js); los de acá
// son sólo para la UI.
import React from 'react';
import {
  Box,
  Collapse,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { NotificationsActive as NotificationsActiveIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export const DEFAULT_REMINDER_CONFIG = {
  enabled: false,
  daysAfterSent: 3,
  repeat: false,
  repeatEveryDays: 3,
  maxReminders: 2,
};

const LIMITS = {
  daysAfterSent: { min: 1, max: 30 },
  repeatEveryDays: { min: 1, max: 30 },
  maxReminders: { min: 1, max: 5 },
};

const clamp = (value, { min, max }) => {
  const n = parseInt(value, 10);
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
};

const TestReminderFields = ({ show = true, value, onChange, disabled = false }) => {
  const { t } = useTranslation();
  const config = value || DEFAULT_REMINDER_CONFIG;

  const set = (patch) => onChange({ ...config, ...patch });

  return (
    <Collapse in={show} unmountOnExit>
      <Box
        sx={{
          mb: 3,
          p: 2,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: config.enabled ? 'action.hover' : 'transparent',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsActiveIcon
            sx={{ color: config.enabled ? 'primary.main' : 'action.disabled' }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!config.enabled}
                onChange={(e) => set({ enabled: e.target.checked })}
                color="primary"
                disabled={disabled}
              />
            }
            label={
              <Box>
                <Typography variant="body2">{t('testReminder.title')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {config.enabled
                    ? t('testReminder.enabledHint')
                    : t('testReminder.disabledHint')}
                </Typography>
              </Box>
            }
          />
        </Box>

        <Collapse in={!!config.enabled} unmountOnExit>
          <Box sx={{ mt: 2, pl: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              type="number"
              size="small"
              label={t('testReminder.daysAfterSent')}
              value={config.daysAfterSent}
              onChange={(e) => set({ daysAfterSent: e.target.value })}
              onBlur={(e) =>
                set({ daysAfterSent: clamp(e.target.value, LIMITS.daysAfterSent) })
              }
              inputProps={LIMITS.daysAfterSent}
              helperText={t('testReminder.daysAfterSentHelp')}
              disabled={disabled}
              // Ancho fijo con flexShrink: 0 — dentro de un contenedor flex, maxWidth
              // no impide que el campo se encoja y trunque el label.
              sx={{ width: 340, flexShrink: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={!!config.repeat}
                  onChange={(e) => set({ repeat: e.target.checked })}
                  color="primary"
                  disabled={disabled}
                />
              }
              label={
                <Typography variant="body2">{t('testReminder.repeat')}</Typography>
              }
            />

            <Collapse in={!!config.repeat} unmountOnExit>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <TextField
                  type="number"
                  size="small"
                  label={t('testReminder.repeatEveryDays')}
                  value={config.repeatEveryDays}
                  onChange={(e) => set({ repeatEveryDays: e.target.value })}
                  onBlur={(e) =>
                    set({ repeatEveryDays: clamp(e.target.value, LIMITS.repeatEveryDays) })
                  }
                  inputProps={LIMITS.repeatEveryDays}
                  disabled={disabled}
                  sx={{ width: 220, flexShrink: 0 }}
                />
                <TextField
                  type="number"
                  size="small"
                  label={t('testReminder.maxReminders')}
                  value={config.maxReminders}
                  onChange={(e) => set({ maxReminders: e.target.value })}
                  onBlur={(e) =>
                    set({ maxReminders: clamp(e.target.value, LIMITS.maxReminders) })
                  }
                  inputProps={LIMITS.maxReminders}
                  helperText={t('testReminder.maxRemindersHelp')}
                  disabled={disabled}
                  sx={{ width: 260, flexShrink: 0 }}
                />
              </Box>
            </Collapse>

            <Typography variant="caption" color="text.secondary">
              {t('testReminder.stopNote')}
            </Typography>
          </Box>
        </Collapse>
      </Box>
    </Collapse>
  );
};

export default TestReminderFields;
